import { getApiOrigin } from "@utils/apiOrigin";
import { getValidAccessToken } from "@utils/authenticatedFetch";

export interface AviationTaxonomyNode {
  id: string;
  kind: string;
  value: string;
  label: string;
  summary: string;
  help_text: string;
  profile_slug?: string | null;
  regulation_packs?: string[];
  overlay_packs?: string[];
  penalty_packs?: string[];
  default_profile_slug?: string;
  activity_domains?: string[];
}

export interface AviationTaxonomyResponse {
  country_code: string;
  vertical: string;
  wizard_schema_version: string;
  steps: Array<{ id: string; title: string; description: string }>;
  families: AviationTaxonomyNode[];
  profiles_by_family: Record<string, AviationTaxonomyNode[]>;
  scope_options: Record<string, AviationTaxonomyNode[]>;
  risk_targets: AviationTaxonomyNode[];
  default_profile_slug_by_family: Record<string, string>;
  family_to_activity_domains: Record<string, string[]>;
}

export interface AviationResolveTargetingResponse {
  wizard_schema_version: string;
  primary_activity_family?: string;
  additional_activity_families?: string[];
  primary_profile_slug?: string;
  activity_domains: string[];
  operation_contexts?: string[];
  asset_types?: string[];
  certificates_or_approvals?: string[];
  risk_targets?: string[];
  regulation_packs: string[];
  overlay_packs: string[];
  penalty_packs: string[];
}

const TAXONOMY_PATH = "/api/v1/aviation/taxonomy";
const RESOLVE_PATH = "/api/v1/aviation/resolve-targeting";

export async function fetchAviationTaxonomy(
  countryCode = "USA",
): Promise<AviationTaxonomyResponse> {
  const base = getApiOrigin();
  if (!base) {
    throw new Error(
      "REACT_APP_API_BASEURL is not set — cannot load aviation taxonomy.",
    );
  }
  const url = new URL(`${base}${TAXONOMY_PATH}`);
  url.searchParams.set("country_code", countryCode);
  const headers: Record<string, string> = { Accept: "application/json" };
  const token = getValidAccessToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  const response = await fetch(url.toString(), { headers });
  if (!response.ok) {
    const detail = await response.text();
    throw new Error(
      `Aviation taxonomy request failed (${response.status}): ${detail}`,
    );
  }
  return response.json() as Promise<AviationTaxonomyResponse>;
}

export async function resolveAviationTargeting(
  payload: Record<string, unknown>,
): Promise<AviationResolveTargetingResponse> {
  const base = getApiOrigin();
  if (!base) {
    throw new Error(
      "REACT_APP_API_BASEURL is not set — cannot resolve aviation targeting.",
    );
  }
  const headers: Record<string, string> = {
    Accept: "application/json",
    "Content-Type": "application/json",
  };
  const token = getValidAccessToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  const response = await fetch(`${base}${RESOLVE_PATH}`, {
    method: "POST",
    headers,
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const detail = await response.text();
    throw new Error(
      `Aviation resolve-targeting failed (${response.status}): ${detail}`,
    );
  }
  return response.json() as Promise<AviationResolveTargetingResponse>;
}
